import { GetState, SetState } from "zustand";
import { BaseService } from "../BaseService";

import environment from "environment";
import type { RolesTypes } from "@hub/common-libs";
import { TopNavigationProps } from "@vfuk/core-top-navigation/dist/TopNavigation.types";
import * as loggedOutNav from "./navbar/loggedOut.json";
import getCookie from "@vfuk/core-helpers/dist/getCookie";
import type { ProductLink } from "@ucchub/ui/src/types";
import { t } from "i18next";

const isLoggedIn = getCookie("Token") || false;
const navBarApi = environment.api.navbar;
const manageAccessTabsApi = environment.api.manageAccessTabs;

type Navbar = TopNavigationProps & {
  primaryNav: {
    productLinks?: Array<ProductLink>;
  };
};

export type LayoutManagementStore = {
  navbar: Navbar;
  accessDenied: {
    isDenied: boolean;
    message: string;
  };
  getNavbar: {
    loading: boolean;
    error: string;
    hasFetched: boolean;
    call: () => Promise<Navbar | null>;
  };
  showProductLinks: boolean;
  showProductLinksManuallySet: boolean;
  toggleProductLinks: (show: boolean) => void;
  fetchTabs: () => Promise<void>;
  getManageAccessTabs: {
    loading: boolean;
    hasFetched: boolean;
    error: string;
  };
  manageAccessTabs: RolesTypes.Tabs[];
};

export function layoutManagementStore<T extends LayoutManagementStore>(
  set: SetState<T>,
  get: GetState<T>
): LayoutManagementStore {
  return {
    accessDenied: {
      isDenied: false,
      message: "",
    },
    navbar: loggedOutNav,
    showProductLinks: false,
    showProductLinksManuallySet: false,
    getNavbar: {
      loading: false,
      hasFetched: false,
      error: "",
      call: () => {
        set((state) => {
          state.getNavbar.loading = true;
        });
        return new Promise<Navbar | null>((resolve, reject) => {
          const hasFetched = get().getNavbar.hasFetched;

          // Only fetch the navbar if the user is logged in or hasn't fetched
          if (!isLoggedIn || hasFetched) {
            set((state) => {
              state.getNavbar.loading = false;
            });
            resolve(get().navbar);
            return;
          }

          // Loading state for the navbar (on full refreshes only)
          set((state) => {
            state.navbar.primaryNav.links = [
              { id: "loading", text: "Loading...", links: [] },
            ];
          });

          BaseService.get(navBarApi)
            .then((res) => {
              set((state) => {
                state.navbar = res.data.data as Navbar;
                // Make sure not to override manully set value
                if (!state.showProductLinksManuallySet) {
                  state.showProductLinks = true;
                }
              });
              resolve(res.data.data as Navbar);
            })
            .catch((error) => {
              // We actually want to know what the error is at least in the console
              // eslint-disable-next-line no-console
              console.error(error);
              set((state) => {
                state.getNavbar.error = t("shared:users:api_failure");
              });
              reject();
            })
            .finally(() => {
              set((state) => {
                state.getNavbar.loading = false;
                state.getNavbar.hasFetched = true;
              });
            });
        });
      },
    },
    manageAccessTabs: [],
    toggleProductLinks: (show) => {
      set((state) => {
        state.showProductLinks = show;
        state.showProductLinksManuallySet = true;
      });
    },
    fetchTabs: async () => {
      set((state) => {
        state.getNavbar.loading = true;
      });
      const { data } = await BaseService.get(manageAccessTabsApi);
      set((state) => {
        state.manageAccessTabs = data.data.tabs;
        state.getManageAccessTabs.loading = false;
        state.getManageAccessTabs.hasFetched = true;
      });
    },
    getManageAccessTabs: {
      loading: false,
      hasFetched: false,
      error: "",
    },
  };
}
